let permissionGranted = false;

// Called directly by the Enable tilt button: iOS requires transient activation.
export async function requestPhoneTilt(host = window) {
  if (!host.isSecureContext || !host.matchMedia('(pointer: coarse)').matches) return false;
  const sensor = host.DeviceOrientationEvent;
  if (!sensor) return false;
  try {
    const granted = typeof sensor.requestPermission !== 'function'
      || await sensor.requestPermission() === 'granted';
    if (granted) {
      permissionGranted = true;
      host.dispatchEvent(new Event('codex-tilt-enabled'));
    }
    return granted;
  } catch { return false; }
}

const clamp = value => Math.max(-1, Math.min(1, value));
const angleDelta = (value, origin) => ((value - origin + 540) % 360) - 180;

// Exact critically damped spring: stable across variable frame rates, with
// inertia when the phone changes direction and no extra animation loop.
export function springTilt(axis, target, dt) {
  const omega = 11, time = Math.max(0, Math.min(dt, .05));
  const offset = axis.position - target;
  const velocity = axis.velocity + omega * offset;
  const decay = Math.exp(-omega * time);
  axis.position = target + (offset + velocity * time) * decay;
  axis.velocity = (axis.velocity - omega * velocity * time) * decay;
}

export function createPhoneTilt({ host = window, onChange = () => {}, enabled = true } = {}) {
  const x = {position: 0, velocity: 0}, y = {position: 0, velocity: 0};
  const aim = {x: 0, y: 0};
  let origin = null, targetX = 0, targetY = 0, listening = false, disposed = false;
  function reset() {
    origin = null;
    targetX = targetY = 0;
    onChange();
  }
  function orientation(event) {
    if (host.document.hidden || !Number.isFinite(event.beta) || !Number.isFinite(event.gamma)) return;
    // eslint-disable-next-line typescript/no-deprecated -- Older Safari exposes the screen angle here.
    const angle = host.screen?.orientation?.angle ?? host.orientation ?? 0;
    if (!origin || origin.angle !== angle) origin = {beta: event.beta, gamma: event.gamma, angle};
    const horizontal = angleDelta(event.gamma, origin.gamma);
    const vertical = angleDelta(event.beta, origin.beta);
    const radians = angle * Math.PI / 180;
    targetX = clamp((horizontal * Math.cos(radians) + vertical * Math.sin(radians)) / 22);
    targetY = clamp((-vertical * Math.cos(radians) + horizontal * Math.sin(radians)) / 22);
    onChange();
  }
  function enable() {
    if (!enabled || listening || disposed || !host.isSecureContext) return;
    listening = true;
    host.addEventListener('deviceorientation', orientation, {passive: true});
  }
  host.addEventListener('codex-tilt-enabled', enable);
  host.addEventListener('orientationchange', reset);
  host.addEventListener('blur', reset);
  host.document.addEventListener('visibilitychange', reset);
  if (permissionGranted) enable();
  return {
    update(dt, active = true) {
      springTilt(x, active ? targetX : 0, dt);
      springTilt(y, active ? targetY : 0, dt);
      aim.x = x.position; aim.y = y.position;
      return aim;
    },
    dispose() {
      disposed = true;
      host.removeEventListener('deviceorientation', orientation);
      host.removeEventListener('codex-tilt-enabled', enable);
      host.removeEventListener('orientationchange', reset);
      host.removeEventListener('blur', reset);
      host.document.removeEventListener('visibilitychange', reset);
    },
  };
}
